import React, { useEffect, useRef } from 'react';
import { useTheme } from '@principal-ade/industry-theme';
import type { Command } from '../types/command.types';
import { CommandItem } from './CommandItem';

interface CommandListProps {
  commands: Command[];
  selectedIndex: number;
  onSelect: (command: Command) => void;
  onHover: (index: number) => void;
  maxResults?: number;
}

/**
 * CommandList - List of filtered commands
 * Displays commands grouped by category with keyboard navigation support
 */
export const CommandList: React.FC<CommandListProps> = ({
  commands,
  selectedIndex,
  onSelect,
  onHover,
  maxResults = 10,
}) => {
  const { theme } = useTheme();
  const listRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // Limit results
  const displayedCommands = commands.slice(0, maxResults);

  // Scroll selected item into view (only within the list container)
  useEffect(() => {
    if (selectedItemRef.current && listRef.current) {
      const item = selectedItemRef.current;
      const container = listRef.current;

      const itemTop = item.offsetTop;
      const itemBottom = itemTop + item.offsetHeight;
      const containerScrollTop = container.scrollTop;
      const containerHeight = container.clientHeight;

      // Scroll only if item is out of view
      if (itemTop < containerScrollTop) {
        // Item is above visible area
        container.scrollTop = itemTop - 8; // 8px padding
      } else if (itemBottom > containerScrollTop + containerHeight) {
        // Item is below visible area
        container.scrollTop = itemBottom - containerHeight + 8;
      }
    }
  }, [selectedIndex]);

  // Group commands by category
  const groupedCommands = displayedCommands.reduce((acc, command, index) => {
    const category = command.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push({ command, index });
    return acc;
  }, {} as Record<string, Array<{ command: Command; index: number }>>);

  // Sort categories
  const categories = Object.keys(groupedCommands).sort((a, b) => {
    // "Other" always last
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });

  return (
    <div
      ref={listRef}
      style={{
        overflowY: 'auto',
        maxHeight: '400px',
        padding: '8px 0',
      }}
    >
      {categories.map((category) => (
        <div key={category}>
          {/* Category Header */}
          {categories.length > 1 && (
            <div
              style={{
                padding: '8px 16px 4px',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                color: theme.colors.textMuted,
                letterSpacing: '0.5px',
              }}
            >
              {category}
            </div>
          )}

          {/* Category Commands */}
          {groupedCommands[category].map(({ command, index }) => (
            <CommandItem
              key={command.id}
              command={command}
              isSelected={index === selectedIndex}
              onClick={() => onSelect(command)}
              onMouseEnter={() => onHover(index)}
              ref={index === selectedIndex ? selectedItemRef : undefined}
            />
          ))}
        </div>
      ))}

      {/* Show count if more results available */}
      {commands.length > maxResults && (
        <div
          style={{
            padding: '8px 16px',
            fontSize: '12px',
            color: theme.colors.textMuted,
            textAlign: 'center',
            borderTop: `1px solid ${theme.colors.border}`,
          }}
        >
          Showing {maxResults} of {commands.length} results
        </div>
      )}
    </div>
  );
};
